import React, { useState } from 'react';
import { Play, Youtube, Maximize, Pause } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface VideoDialogProps {
  videoSrc?: string;
  videoTitle?: string;
  className?: string;
  floating?: boolean;
  embedded?: boolean;
}

const VideoDialog: React.FC<VideoDialogProps> = ({
  videoSrc = "https://res.cloudinary.com/dnbk7yjo4/video/upload/v1750794590/Migrante_App_Intro_Video_Vs3_1_1_f1akmg.mp4",
  videoTitle = "¿Cómo usar MigranteApp?",
  className,
  floating = false,
  embedded = false
}) => {
  const [videoError, setVideoError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);

  const togglePlay = (videoElement: HTMLVideoElement) => {
    if (videoElement.paused) {
      videoElement.muted = false; // Enable audio when playing
      // Only set currentTime to 1 if the video hasn't started yet
      if (!hasStarted) {
        videoElement.currentTime = 1;
        setHasStarted(true);
      }
      videoElement.play();
      setIsPlaying(true);
    } else {
      videoElement.pause();
      setIsPlaying(false);
    }
  };

  // Embedded video component
  if (embedded) {
    return (
      <div className="relative z-40 my-0">
        <div className={cn(
          "relative w-full max-w-4xl h-96 mx-auto rounded-xl overflow-hidden shadow-lg border-2 border-white",
          "transition-all duration-300 hover:scale-105 hover:shadow-xl",
          "cursor-pointer",
          className
        )}>
          <video 
            src={videoSrc}
            muted
            playsInline
            className="w-full h-full object-cover"
            onError={() => setVideoError(true)}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => {
              setIsPlaying(false);
              setHasStarted(false); // Reset for next play
            }}
            onLoadedMetadata={(e) => {
              // Set initial time to 1 second when video loads (only once)
              if (!hasStarted) {
                e.currentTarget.currentTime = 1;
              }
            }}
            controls
            controlsList="nodownload"
          />
          
          {/* Clickable overlay for play/pause - only covers the upper part to avoid interfering with controls */}
          <div 
            className="absolute inset-0 bottom-12 flex items-center justify-center z-10"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const videoElement = e.currentTarget.parentElement?.querySelector('video') as HTMLVideoElement;
              if (videoElement) {
                togglePlay(videoElement);
              }
            }}
          >
            {/* Big centered play button - only show when not playing */}
            {!isPlaying && (
              <div className="w-24 h-24 md:w-28 md:h-28 bg-blue-600/90 hover:bg-blue-700 rounded-full flex items-center justify-center shadow-lg transform transition-transform hover:scale-105 active:scale-95">
                <Play className="h-12 w-12 md:h-14 md:w-14 text-white ml-1" />
              </div>
            )}
          </div>

          <div className="absolute inset-0 flex flex-col justify-between p-2.5 pointer-events-none z-20">
            <div className="flex justify-end">
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7 rounded-full bg-white/20 hover:bg-white/30 pointer-events-auto">
                    <Maximize className="h-3.5 w-3.5 text-white" />
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-[800px] p-0 overflow-hidden rounded-xl border-0 shadow-2xl">
                  <DialogHeader className="p-6 pb-2 bg-gradient-to-r from-blue-50 to-blue-100">
                    <DialogTitle className="text-xl text-blue-800 font-semibold">{videoTitle}</DialogTitle>
                  </DialogHeader>
                  <div className="aspect-video w-full relative bg-black">
                    {videoError ? (
                      <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100 p-8 text-center">
                        <div className="mb-6 text-gray-400">
                          <svg className="w-20 h-20 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-medium mb-3 text-gray-800">No se pudo cargar el video</h3>
                        <p className="text-gray-600 mb-6">
                          Ha ocurrido un problema al cargar el video. Por favor, inténtelo de nuevo más tarde.
                        </p>
                      </div>
                    ) : (
                      <video 
                        width="100%" 
                        height="100%" 
                        src={videoSrc}
                        title={videoTitle}
                        controls
                        controlsList="nodownload"
                        className="w-full h-full object-cover"
                        onError={() => setVideoError(true)}
                        onLoadedMetadata={(e) => {
                          e.currentTarget.currentTime = 1;
                        }}
                      >
                        Su navegador no soporta la reproducción de videos.
                      </video>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <div>
              <h3 className="text-xs font-medium text-white">{videoTitle}</h3>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button className={className}>Ver Video</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[800px] p-0 overflow-hidden rounded-xl border-0 shadow-2xl">
        <DialogHeader className="p-6 pb-2 bg-gradient-to-r from-blue-50 to-blue-100">
          <DialogTitle className="text-xl text-blue-800 font-semibold">{videoTitle}</DialogTitle>
        </DialogHeader>
        <div className="aspect-video w-full relative bg-black">
          {videoError ? (
            <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100 p-8 text-center">
              <div className="mb-6 text-gray-400">
                <svg className="w-20 h-20 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium mb-3 text-gray-800">No se pudo cargar el video</h3>
              <p className="text-gray-600 mb-6">
                Ha ocurrido un problema al cargar el video. Por favor, inténtelo de nuevo más tarde.
              </p>
            </div>
          ) : (
            <video 
              width="100%" 
              height="100%" 
              src={videoSrc}
              title={videoTitle}
              controls
              controlsList="nodownload"
              className="w-full h-full object-cover"
              onError={() => setVideoError(true)}
            >
              Su navegador no soporta la reproducción de videos.
            </video>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VideoDialog;
